/**
 * Edge predicate helpers for network view profiles.
 *
 * Why this exists: `NetworkHydrator.EDGE_TYPE_MAP` collapses several walker
 * edge kinds (`connects`, `reads-from`, `routes-to`, `encrypts`, `logs-to`,
 * etc.) onto a smaller set of universal `NetworkEdgeType` values
 * (`depends_on`, `data_flow`, `routes_to`, `association`). The original
 * walker kind survives only on `props.infraEdgeKind`.
 *
 * If a chain rule's `via` list contains walker kinds and the engine matches
 * only against `edge.type`, the chain silently won't fire on real hydrated
 * graphs — every test fixture must hand-construct edges with `type:
 * 'connects'` and the bug stays invisible. Use these helpers for any
 * profile rule that wants to talk in walker-kind vocabulary.
 */
import type { GraphEdge } from '../../../lib/graph/types';
import type { EdgePredicate } from '../../../lib/profiles';

/**
 * Returns an EdgePredicate that matches when the edge's `type` OR
 * `props.infraEdgeKind` is in the given set. Use as `via` on chain rules.
 *
 * @example
 *   via: matchEdgeKinds(['connects', 'depends_on', 'routes_to'])
 */
export function matchEdgeKinds(kinds: readonly string[]): EdgePredicate {
  const set = new Set(kinds);
  return (edge: GraphEdge, props: Record<string, unknown>): boolean => {
    if (set.has(edge.type)) return true;
    const kind = props.infraEdgeKind;
    return typeof kind === 'string' && set.has(kind);
  };
}
